import {API_ENDPOINTS} from '../constants';
import {getAuthToken} from './account';

export interface IDatabaseField extends IDatabaseCreateField {
    id: string;
    value: any | undefined | null;
}

export interface IDatabaseCore {
    title: string;
    category: string;
    link: string;
    access: 'public' | 'private';
    filters: string[];
    settings: {
        layout: string;
        allowAttachments: boolean;
    };
}
export interface IDatabaseShared extends IDatabaseBase, IDatabaseCore {}

export interface IDatabaseBase {
    created?: number;
    name: string;
    title: string;
    accountName?: string;
    account?: {
        city: string;
        name: string;
        title: string;
    } | string;
}
export interface IDatabase extends IDatabaseShared {
    fields: IDatabaseField[];
}
export interface IDatabaseCreate extends IDatabaseCore {
    fields: IDatabaseCreateField[];
}

export interface IDatabaseCreateField {
    group: string;
    name: string;
    label: string;
    type: string;
    required: boolean;
    predefined: boolean;
    selection: string;
    options: string[];
    extra: string;
}

const baseCache = {
    databaseList: null,
    databaseListpartnered: null,
    databaseListaccount: null,
    databases: null,
};
const at: string = getAuthToken() || '';
let cache = {
    [at]: {...baseCache},
};

const getFromCache = (key: string, needle?: string) => {
    const authToken: string = getAuthToken() || '';
    if (!cache[authToken]) resetCache();
    if (needle) return (cache[authToken][key] || []).find((x: any) => x.name === needle);
    return cache[authToken][key];
};
const addToCache = (key: string, value: any) => {
    const authToken: string = getAuthToken() || '';
    if (!cache[authToken]) resetCache();
    if (Array.isArray(cache[authToken][key]))
        cache[authToken][key] = [
            ...cache[authToken][key],
            ...(Array.isArray(value) ? value : [value]),
        ];
    else if (Object.keys(cache[authToken]).includes(key))
        cache[authToken][key] =
            typeof value === 'function' ? value() : Array.isArray(value) ? [...value] : [value];
    else console.error('DATABASE CACHE MISSING KEY', key);
};
export const resetCache = () => {
    const authToken: string = getAuthToken() || '';
    cache = {[authToken]: {...baseCache}};
};

export const format = (group: string, fieldName: string) =>
    `${formatGroup(group)}_${fieldName.toLowerCase()}`;
export const formatGroup = (group: string) => `${group.replace(/[\W_]+/g, '_')}`;

export const create: (data: IDatabaseCreate) => Promise<{success: boolean; message: string}> =
    async (data: IDatabaseCreate) => {
        const result = {
            success: false,
            message: '',
        };
        try {
            const request = await fetch(API_ENDPOINTS.databases(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify(data),
            });
            console.log('DATABASE-CREATE', result, data);
            if (request.ok) {
                result.success = true;
            }
        } catch (ex) {
            console.error(ex);
            result.success = false;
            alert('There was an error creating your database. Please reload and try again');
        }
        resetCache();
        return result;
    };

export const getAnalyticsToken = async (databaseName: string): Promise<string> => {
    const request = await fetch(API_ENDPOINTS.analytics(databaseName), {
        method: 'GET',
    });
    return request.text();
};

export const update: (
    name: string,
    data: IDatabaseCreate
) => Promise<{success: boolean; message: string}> = async (name: string, data: IDatabaseCreate) => {
    const result = {
        success: false,
        message: '',
    };
    try {
        const request = await fetch(API_ENDPOINTS.databases(name), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify(data),
        });
        console.log('DATABASE-UPDATED', result, data);
        if (request.ok) {
            result.success = true;
        }
    } catch (ex) {
        console.error(ex);
        result.success = false;
        alert('There was an error creating your database. Please reload and try again');
    }
    resetCache();
    return result;
};

export const get: (
    id?: string,
    accountName?: string,
    type?: 'account' | 'partnered' | ''
) => Promise<{
    success: boolean;
    database: IDatabase | null;
    databases: IDatabaseBase[] | null;
}> = async (id, accountName, type = '') => {
    const result: {
        success: boolean;
        database: IDatabase | null;
        databases: IDatabaseBase[] | null;
    } = {
        success: false,
        database: null,
        databases: null,
    };
    if (id)
        try {
            const cacheHit = await getFromCache('databases', id);
            if (cacheHit) {
                result.success = true;
                result.database = {...cacheHit};
            } else {
                const request = await fetch(`${API_ENDPOINTS.databases(id, accountName)}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${getAuthToken()}`,
                    },
                });
                if (request.ok) {
                    result.success = true;
                    result.database = {...(await request.json())};
                    addToCache('databases', result.database);
                }
            }
        } catch (ex) {
            console.error(ex);
            result.success = false;
            alert('There was an error fetching your database. Please reload and try again');
        }
    else if (!id && accountName) {
        try {
            const request = await fetch(API_ENDPOINTS.accountDatabases(accountName), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
            });
            if (request.ok) {
                result.success = true;
                result.databases = [...(await request.json())];
            }
        } catch (ex) {
            console.error(ex);
            result.success = false;
            alert('There was an error fetching your databases. Please reload and try again');
        }
        return result;
    } else {
        try {
            const cacheHit = await getFromCache(`databaseList${type}`);
            if (cacheHit) {
                result.success = true;
                result.databases = [
                    ...(type === 'partnered' ? cacheHit.map((x: any) => x.database) : cacheHit),
                ];
            } else {
                const loadindDatabases = async () => {
                    try {
                        const request = await fetch(API_ENDPOINTS.databases(type), {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${getAuthToken()}`,
                            },
                        });
                        if (request.ok) {
                            return request.json();
                        }
                        return null;
                    } catch (ex) {
                        console.error(ex);
                        result.success = false;
                        alert(
                            'There was an error fetching your databases. Please reload and try again'
                        );
                    }
                    return null;
                };
                addToCache(`databaseList${type}`, loadindDatabases);
                const databaseList = await getFromCache(`databaseList${type}`);
                if (databaseList) {
                    result.success = true;
                    const d =
                        type === 'partnered'
                            ? databaseList.map((x: any) => x.database)
                            : databaseList;
                    result.databases = Array.isArray(d) ? [...d] : [];
                }
            }
        } catch (ex) {
            console.error(ex);
            result.success = false;
            alert('There was an error fetching your databases. Please reload and try again');
        }
    }
    return result;
};



// WEBPACK FOOTER //
// ./src/services/database.ts